import React, { useState, useMemo, useEffect, useRef } from 'react';
import * as Icons from 'lucide-react';
import { Search, X } from 'lucide-react';

// Curated set of icons that are actually useful for content sections.
// Keeps the picker fast (Lucide has 1000+; the full set makes the grid unusable).
const CURATED_ICONS = [
  // Trust / policy / mission
  'shield', 'shield-check', 'lock', 'key', 'flag', 'award', 'gem', 'crown',
  // Global / connection
  'globe', 'map', 'map-pin', 'compass', 'radio', 'signal', 'wifi', 'satellite',
  // People / teams
  'users', 'user', 'user-check', 'user-plus', 'handshake', 'heart-handshake',
  // Growth / performance
  'trending-up', 'bar-chart', 'bar-chart-3', 'pie-chart', 'activity', 'zap', 'flame', 'sparkles',
  // Energy / atomic / science
  'atom', 'sun', 'battery-charging', 'plug', 'lightbulb', 'gauge',
  // Ideas / research
  'brain', 'graduation-cap', 'book-open', 'library', 'microscope', 'flask-conical',
  // Comms
  'mic', 'megaphone', 'message-square', 'send', 'mail', 'phone', 'video',
  // Business / commerce
  'briefcase', 'building-2', 'store', 'wallet', 'credit-card', 'dollar-sign',
  // Time / process
  'clock', 'calendar', 'calendar-check', 'timer', 'hourglass',
  // Steps / journey
  'rocket', 'target', 'crosshair', 'route', 'workflow', 'list-checks', 'file-signature',
  // Star / featured
  'star', 'bookmark', 'trophy', 'medal', 'ribbon',
  // Layout
  'layers', 'layout-grid', 'layout-dashboard', 'list-ordered', 'grid-3x3',
  // Nav / arrows
  'arrow-right', 'chevron-right', 'check', 'check-circle',
];

const nameToPascal = (name) =>
  name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join('');

const iconFor = (name) => {
  if (!name) return null;
  return Icons[nameToPascal(name)] || null;
};

const IconPicker = ({ value, onChange, testId, buttonClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CURATED_ICONS;
    return CURATED_ICONS.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const SelectedIcon = iconFor(value) || Icons.Sparkles;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId || 'icon-picker-button'}
        className={`w-full flex items-center gap-2 px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-left text-sm ${buttonClassName}`}
      >
        <span className="w-8 h-8 rounded flex items-center justify-center bg-slate-100 text-[#00457f] flex-shrink-0">
          <SelectedIcon className="w-4 h-4" strokeWidth={2.25} />
        </span>
        <span className="flex-1 truncate">{value || 'Pick an icon…'}</span>
        <span className="text-xs text-slate-400">▾</span>
      </button>
      {open && (
        <div
          className="absolute z-30 mt-2 left-0 right-0 min-w-[280px] max-h-[340px] rounded-xl border bg-white shadow-2xl flex flex-col"
          data-testid="icon-picker-popover"
        >
          <div className="p-2 border-b flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search icons…"
              className="flex-1 text-sm outline-none"
              data-testid="icon-picker-search"
            />
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="text-xs text-slate-400 hover:text-slate-600 px-1"
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-6 gap-1 p-2 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="col-span-6 text-center text-xs text-slate-500 py-6">
                No icons match &quot;{query}&quot;
              </div>
            )}
            {filtered.map((name) => {
              const Icon = iconFor(name);
              if (!Icon) return null;
              const active = name === value;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onChange(name); setOpen(false); }}
                  title={name}
                  data-testid={`icon-option-${name}`}
                  className={`aspect-square rounded-md flex items-center justify-center transition-colors ${
                    active
                      ? 'bg-[#00457f] text-white ring-2 ring-[#00457f]'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={2.25} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IconPicker;
